import { useModal } from '@hooks/useModal'
import { Switch } from 'components/buttons'
import { TwoColumn } from 'components/layout'
import { toast } from 'lib/toasts'
import { useTranslation } from 'react-i18next'

interface Props {
  approvalDomain?: { id: number; name: string; autoApproval: boolean }
  approvalDomainType: 'approval group' | 'project'
  mutateFn: (
    approvalDomainId: number,
    options: { onSuccess: () => void; onError: () => void },
  ) => void
}

export const AutoApprovalSection = ({
  approvalDomain,
  approvalDomainType,
  mutateFn,
}: Props) => {
  const { t } = useTranslation()
  const modal = useModal()

  const confirmThenToggle = () => {
    if (!approvalDomain) return

    const toggleAutoApproval = () => {
      mutateFn(approvalDomain.id, {
        onSuccess: () => {
          toast({
            title: t('common.autoApproval.autoApprovalUpdated'),
            variant: 'success',
            content: t(
              approvalDomain.autoApproval
                ? 'common.autoApproval.autoApprovalDisabledFor'
                : 'common.autoApproval.autoApprovalEnabledFor',
              { name: approvalDomain.name },
            ),
          })
        },
        onError: () => {
          toast({
            title: t('common.error'),
            variant: 'error',
            content: t('common.autoApproval.failedToUpdateAutoApproval'),
          })
        },
      })
    }

    modal.confirm({
      title: t(
        approvalDomain.autoApproval
          ? 'components.autoApprovalToggle.disablingAutoApproval'
          : 'components.autoApprovalToggle.enablingAutoApproval',
      ),
      content: (
        <div>
          <p>
            {t(
              approvalDomain.autoApproval
                ? 'components.autoApprovalToggle.disablingAutoApprovalRequiresManualApproval'
                : 'components.autoApprovalToggle.enablingAutoApprovalWillApprovePendingAndFuture',
              { approvalDomainType },
            )}
          </p>
          <div className="text-lg font-semibold mt-4">
            {approvalDomain.name}
          </div>
        </div>
      ),
      confirmText: t(
        approvalDomain.autoApproval
          ? 'components.autoApprovalToggle.disableAutoApproval'
          : 'components.autoApprovalToggle.enableAutoApproval',
      ),
      onConfirm: toggleAutoApproval,
    })
  }

  return (
    <TwoColumn
      label={t('common.autoApproval.autoApprovalLabel')}
      description={t('common.autoApproval.autoApprovalDescription')}
    >
      <Switch
        onChange={confirmThenToggle}
        checked={approvalDomain?.autoApproval}
      />
    </TwoColumn>
  )
}
